import React, { useState, useEffect, useRef } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import es from 'react-phone-number-input/locale/es.json';
import { X, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const countryOptions = getCountries().map(country => ({
  code: country,
  callingCode: `+${getCountryCallingCode(country)}`,
  label: es[country] || country,
  flag: country
}));

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export function ProfileCompletionModal({ 
  isOpen, 
  onClose, 
  onComplete 
}: ProfileCompletionModalProps) {
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('AR');
  const [areaCode, setAreaCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (!isOpen && dialogRef.current) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  // Validación reactiva en tiempo real
  const validateField = (fieldName: string, value: string): boolean => {
    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) error = 'El nombre es requerido';
        break;
      case 'phoneNumber':
        if (!value.trim()) {
          error = 'El número de teléfono es requerido';
        } else if (value.trim().length < 4) {
          error = 'El número debe tener al menos 4 dígitos';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Email inválido';
        }
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error === '';
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) newErrors.name = 'El nombre es requerido';
    
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'El número de teléfono es requerido';
    } else if (phoneNumber.trim().length < 4) {
      newErrors.phoneNumber = 'El número debe tener al menos 4 dígitos';
    }
    
    if (!email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      onComplete?.();
      onClose?.();
    }, 1000);
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };

  // Handlers reactivos para cada campo
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    validateField('name', value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAreaCode(e.target.value);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value);
    validateField('phoneNumber', value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    validateField('email', value);
  };

  const getCurrentCountryCallingCode = (): string => {
    const option = countryOptions.find(opt => opt.code === selectedCountry);
    return option ? option.callingCode : '+34';
  };

  return (
    <dialog 
      ref={dialogRef} 
      className="backdrop:bg-black/60 bg-transparent p-0 max-w-[500px] w-[calc(100%-2rem)] rounded-2xl shadow-2xl outline-none"
      onClick={(e) => {
        // Cerrar al hacer clic en el backdrop
        if (e.target === dialogRef.current) {
          handleClose();
        }
      }}
    >
      <div className="bg-card rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-muted/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-foreground mb-1">
                Completa tus datos
              </h3>
              <p className="text-sm text-muted-foreground">
                Iniciarás una conversación con CeLia, nuestra asesora virtual.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 rounded-md hover:bg-muted"
              disabled={isSubmitting}
              type="button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nombre y Apellido */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Nombre
              </label>
              <Input
                type="text"
                value={name}
                onChange={handleNameChange}
                disabled={isSubmitting}
                className={cn(
                  errors.name && 'border-destructive focus-visible:ring-destructive'
                )}
                placeholder="Ingresa tu nombre"
              />
              {errors.name && (
                <p className="text-xs text-destructive animate-in slide-in-from-top-1">
                  {errors.name}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Apellido
              </label>
              <Input
                type="text"
                value={lastName}
                onChange={handleLastNameChange}
                disabled={isSubmitting}
                placeholder="Ingresa tu apellido"
              />
            </div>
          </div>

          {/* Teléfono */}
          <div className="grid grid-cols-[1.5fr_0.8fr_1.2fr] gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Código de país
              </label>
              <select
                value={selectedCountry}
                onChange={handleCountryChange}
                disabled={isSubmitting}
                className={cn(
                  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                  "ring-offset-background",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                )}
              >
                {countryOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    {getCountryFlag(option.code)} {option.label} ({option.callingCode})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Cód. área
              </label>
              <Input
                type="number"
                placeholder="11"
                value={areaCode}
                onChange={handleAreaCodeChange}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Número
              </label>
              <Input
                type="number"
                placeholder="12345678"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                disabled={isSubmitting}
                className={cn(
                  errors.phoneNumber && 'border-destructive focus-visible:ring-destructive'
                )}
              />
              {errors.phoneNumber && (
                <p className="text-xs text-destructive animate-in slide-in-from-top-1">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
              className={cn(
                errors.email && 'border-destructive focus-visible:ring-destructive'
              )}
              placeholder="tu@email.com"
            />
            {errors.email && (
              <p className="text-xs text-destructive animate-in slide-in-from-top-1">
                {errors.email}
              </p>
            )}
          </div>

          {/* Info Box */}
          <div className="flex gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex-shrink-0 mt-0.5">
              <Info className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <h4 className="text-sm font-medium text-foreground">
                ¿Por qué necesitamos esta información?
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Estos datos nos permiten brindarte una experiencia personalizada y contactarte 
                directamente para soporte especializado cuando lo necesites.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 text-base font-medium"
          >
            {isSubmitting ? 'Guardando...' : 'Iniciar conversación'}
          </Button>
        </form>
      </div>
    </dialog>
  );
}

